/* Native reveal-on-scroll-up header. One coalesced frame; no polling or requests. */
if (!customElements.get("sticky-header")) {
  customElements.define("sticky-header", class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.header = this.closest(".section-header") || this;
      this.headerElement = this.querySelector(".header") || this;
      this.stickyDisabled = this.getAttribute("disabled") === "true";
      this.alwaysSticky = this.dataset.alwaysOn === "true";
      this.mainNavToggle = this.querySelector("main-nav-toggle");
      this.currentScrollTop = Math.max(0, window.scrollY);
      this.scrollDelta = 0;
      this.preventReveal = false;
      this.state = "normal";
      this.motionUntil = 0;
      this.lastHeight = this.lastVisibleHeight = this.lastState = undefined;
      this.anchor = document.createElement("div");
      this.anchor.setAttribute("aria-hidden", "true");
      this.anchor.dataset.stickyHeaderAnchor = "";
      this.header.before(this.anchor);
      this.onScrollHandler = () => this.schedule();
      this.onResize = () => { this.measure(); this.schedule(); };
      this.onInteraction = () => {
        if (!this.stickyDisabled && this.isInteracting()) this.reveal();
        this.schedule();
      };
      this.hideHeaderOnScrollUp = () => {
        this.preventReveal = true;
        clearTimeout(this.revealTimer);
        this.revealTimer = setTimeout(() => { this.preventReveal = false; }, 100);
      };
      this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
      this.addEventListener("focusin", this.onInteraction);
      this.addEventListener("focusout", this.onInteraction);
      this.addEventListener("toggle", this.onInteraction, true);
      document.addEventListener("cart-drawer:open", this.onInteraction);
      document.addEventListener("cart-drawer:close", this.onInteraction);
      window.addEventListener("scroll", this.onScrollHandler, {passive: true});
      window.addEventListener("resize", this.onResize, {passive: true});
      window.addEventListener("pageshow", this.onResize);
      if (typeof ResizeObserver === "function") {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.headerElement);
      }
      this.measure();
      if (this.alwaysSticky && !this.stickyDisabled) this.applyState("always");
      else if (this.currentScrollTop > this.headerBounds.bottom && !this.stickyDisabled) this.hide();
      this.schedule();
    }

    disconnectedCallback() {
      if (!this.active) return;
      this.active = false;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame || 0);
      this.frame = 0;
      clearTimeout(this.revealTimer);
      this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
      this.removeEventListener("focusin", this.onInteraction);
      this.removeEventListener("focusout", this.onInteraction);
      this.removeEventListener("toggle", this.onInteraction, true);
      document.removeEventListener("cart-drawer:open", this.onInteraction);
      document.removeEventListener("cart-drawer:close", this.onInteraction);
      window.removeEventListener("scroll", this.onScrollHandler);
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("pageshow", this.onResize);
      this.header.classList.remove("section-header-sticky", "section-header-hidden", "section-header-sticky-always", "animate");
      this.anchor.remove();
      if (!document.querySelector("sticky-header")) {
        document.documentElement.style.removeProperty("--ss-header-height");
        document.documentElement.style.removeProperty("--ss-header-visible-height");
        document.dispatchEvent(new CustomEvent("saber:header-state", {detail: {height: 0, visibleHeight: 0, state: "disconnected"}}));
      }
    }

    measure() {
      const top = this.anchor.getBoundingClientRect().top + window.scrollY;
      this.height = Math.ceil(this.headerElement.getBoundingClientRect().height);
      this.headerBounds = {top, bottom: top + this.height};
    }

    schedule() {
      if (!this.active || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.active) return;
        this.onScroll();
        this.updateMetrics();
        if (performance.now() < this.motionUntil) this.schedule();
      });
    }

    isInteracting() {
      return this.contains(document.activeElement) ||
        Boolean(this.querySelector('.ss-departments[open], dialog[open], predictive-search[open]')) ||
        Boolean(this.querySelector("predictive-search")?.isOpen) ||
        document.body.classList.contains("drawer-open");
    }

    onScroll() {
      const scrollTop = Math.max(0, window.scrollY);
      const delta = scrollTop - this.currentScrollTop;
      this.currentScrollTop = scrollTop;
      if (this.stickyDisabled) { this.reset(); return; }
      if (this.alwaysSticky) { this.applyState("always"); return; }
      if (scrollTop <= this.headerBounds.top) { this.scrollDelta = 0; this.reset(); return; }
      if (this.isInteracting()) { this.scrollDelta = 0; this.reveal(); return; }
      this.scrollDelta = Math.sign(delta) === Math.sign(this.scrollDelta) ? this.scrollDelta + delta : delta;
      if (Math.abs(this.scrollDelta) < 6) return;
      if (scrollTop > this.headerBounds.bottom) {
        if (this.scrollDelta > 0) this.hide();
        else if (!this.preventReveal) this.reveal();
      }
      this.scrollDelta = 0;
    }

    hide() {
      if (this.alwaysSticky || this.isInteracting()) return;
      this.applyState("hidden");
    }

    reveal() {
      if (!this.stickyDisabled) this.applyState(this.alwaysSticky ? "always" : "visible");
    }

    reset() { this.applyState("normal"); }

    applyState(state) {
      if (this.state === state) return;
      const previous = this.state;
      this.state = state;
      this.header.classList.toggle("section-header-sticky", state === "hidden" || state === "visible");
      this.header.classList.toggle("section-header-hidden", state === "hidden");
      this.header.classList.toggle("section-header-sticky-always", state === "always");
      this.header.classList.toggle("animate", state === "visible" || (state === "hidden" && previous === "visible"));
      if (state === "hidden") this.mainNavToggle?.enable();
      if (state === "normal") this.mainNavToggle?.disable();
      this.motionUntil = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : performance.now() + 180;
      this.schedule();
    }

    updateMetrics() {
      const rectangle = this.header.getBoundingClientRect();
      const visibleHeight = rectangle.top < window.innerHeight && rectangle.bottom > 0 ? Math.max(0, Math.ceil(Math.min(window.innerHeight, rectangle.bottom))) : 0;
      const root = document.documentElement.style;
      const changed = this.lastHeight !== this.height || this.lastVisibleHeight !== visibleHeight || this.lastState !== this.state;
      if (!changed) return;
      root.setProperty("--ss-header-height", this.height + "px");
      root.setProperty("--ss-header-visible-height", visibleHeight + "px");
      this.lastHeight = this.height;
      this.lastVisibleHeight = visibleHeight;
      this.lastState = this.state;
      document.dispatchEvent(new CustomEvent("saber:header-state", {detail: {height: this.height, visibleHeight, state: this.state}}));
    }
  });
}

if (!customElements.get("main-nav-toggle")) {
  customElements.define(
    "main-nav-toggle",
    class MainNavToggle extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector("#header");
        this.stickyHeader = document.querySelector("sticky-header");
        this.button = this.querySelector(".button-main-nav-toggle");
        this.navigationWrapper = document.querySelector(".navigation-wrapper");

        if (!this.navigationWrapper) {
          return;
        }

        this.navigationWrapper.style.setProperty(
          "--navigation-height",
          this.navigationWrapper.clientHeight + 2 + "px",
        );

        this.button.addEventListener("click", () => this.onToggle());
      }

      onToggle(state) {
        if (this.navigationWrapper.classList.contains("is-animating") || !this.stickyHeader) {
          return;
        }

        this.navigationWrapper.classList.add("is-animating");

        if (state != null) {
          if (state === "collapse") {
            this.collapse();
          } else if (state === "expand") {
            this.expand();
          }
        } else {
          this.isCollapsed() ? this.expand() : this.collapse();
        }

        setTimeout(() => {
          this.navigationWrapper.classList.remove("is-animating");
        }, 300);
      }

      enable() {
        if (!this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.add("header-nav-collapse-enabled");
          this.collapse();
        }
      }

      disable() {
        if (this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.remove("header-nav-collapse-enabled");
          this.expand();
        }
      }

      expand() {
        this.header.classList.remove("navigation-collapsed");
        this.button.setAttribute("aria-expanded", "true");
      }

      collapse() {
        this.classList.add("is-visible");
        this.header.classList.add("navigation-collapsed");
        this.button.setAttribute("aria-expanded", "false");
      }

      isCollapsed() {
        return this.header.classList.contains("navigation-collapsed");
      }
    },
  );
}
